import React, { useEffect, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { RiHomeFill } from 'react-icons/ri';
import { MdSettings, MdAdminPanelSettings, MdDashboard } from 'react-icons/md';
import { IoIosArrowDown, IoIosArrowForward } from 'react-icons/io';
import { IoNotificationsOutline } from 'react-icons/io5';
import { FiLogIn, FiLogOut } from 'react-icons/fi';
import logo from '../assets/logo.png';
import logoWhite from '../assets/logowhite.png';
import { categories } from '../utils/data';
import { useUser } from '../context/UserContext';
import { useTheme } from '../context/ThemeContext';

const isNotActiveStyle =
  'flex items-center px-5 gap-3 text-gray-500 hover:text-blue-600 dark:hover:text-blue-300 transition-all duration-200 capitalize';
const isActiveStyle =
  'flex items-center px-5 gap-3 font-extrabold border-r-4 border-blue-500 dark:border-blue-300 bg-blue-50 dark:bg-slate-900 text-blue-700 dark:text-blue-200 transition-all duration-200 capitalize';

const Sidebar = ({ closeToggle }) => {
  const { user } = useUser();
  const { theme } = useTheme();

  const [categoriesOpen, setCategoriesOpen] = useState(false);

  const handleCloseSidebar = () => {
    if (closeToggle) closeToggle(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  const toggleCategories = () => {
    setCategoriesOpen(!categoriesOpen);
  };

  useEffect(() => {
    const html = document.documentElement;
    if (theme === 'dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  }, [theme]);

  return (
    <div className="flex flex-col justify-between h-full overflow-y-scroll bg-white dark:bg-slate-950 dark:text-white min-w-210 hide-scrollbar">
      <div className="flex flex-col">
        <Link
          to="/"
          className="flex px-4 my-4 pt-1 w-190 items-center"
          onClick={handleCloseSidebar}
        >
          <img
            src={theme === 'dark' ? logoWhite : logo}
            alt="logo"
            className="w-full max-w-[140px] mx-auto"
          />
        </Link>

        <nav className="flex flex-col gap-2">
          <NavLink
            to="/"
            className={({ isActive }) =>
              isActive ? isActiveStyle : isNotActiveStyle
            }
            onClick={handleCloseSidebar}
          >
            <RiHomeFill className="text-xl" />
            Home
          </NavLink>

          {/* 🔔 Notifications */}
          {user && (
            <NavLink
              to="/notifications"
              className={({ isActive }) =>
                isActive ? isActiveStyle : isNotActiveStyle
              }
              onClick={handleCloseSidebar}
            >
              <IoNotificationsOutline className="text-xl" />
              Notifications
            </NavLink>
          )}

          <NavLink
            to="/settings"
            className={({ isActive }) =>
              isActive ? isActiveStyle : isNotActiveStyle
            }
            onClick={handleCloseSidebar}
          >
            <MdSettings className="text-xl" />
            Settings
          </NavLink>

          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              isActive ? isActiveStyle : isNotActiveStyle
            }
            onClick={handleCloseSidebar}
          >
            <MdDashboard className="text-xl" />
            Dashboard
          </NavLink>

          {user?.role === 'admin' && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                isActive ? isActiveStyle : isNotActiveStyle
              }
              onClick={handleCloseSidebar}
            >
              <MdAdminPanelSettings className="text-xl" />
              <span>Admin Panel</span>
            </NavLink>
          )}

          {!user && (
            <NavLink
              to="/login"
              className={({ isActive }) =>
                isActive ? isActiveStyle : isNotActiveStyle
              }
              onClick={handleCloseSidebar}
            >
              <FiLogIn className="text-xl" />
              <span>Login</span>
            </NavLink>
          )}

          <NavLink
            to="/register"
            className={({ isActive }) =>
              isActive ? isActiveStyle : isNotActiveStyle
            }
            onClick={handleCloseSidebar}
          >
            <span>Create An Account</span>
          </NavLink>

          <button
            type="button"
            onClick={toggleCategories}
            className="flex items-center px-5 gap-3 text-gray-500 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-300 transition-all duration-200 capitalize focus:outline-none"
          >
            <span>Discover Categories</span>
            {categoriesOpen ? <IoIosArrowDown /> : <IoIosArrowForward />}
          </button>

          {categoriesOpen && (
            <div className="flex flex-col ml-8 gap-2 mt-1">
              {categories.slice(0, categories.length - 1).map((category) => (
                <NavLink
                  to={`/category/${category.name}`}
                  className={({ isActive }) =>
                    isActive ? isActiveStyle : isNotActiveStyle
                  }
                  key={category.name}
                  onClick={handleCloseSidebar}
                >
                  <img
                    src={category.image}
                    className="w-8 h-8 rounded-full shadow-sm"
                    alt={category.name}
                  />
                  {category.name}
                </NavLink>
              ))}
            </div>
          )}
        </nav>
      </div>

      {user && (
        <div className="flex flex-col gap-2 mb-4">
          <Link
            to={`user-profile/${user._id}`}
            className="flex my-2 gap-3 p-2 items-center rounded-lg mx-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold shadow-lg hover:shadow-xl transition-transform duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
            onClick={handleCloseSidebar}
          >
            <img
              src={user.image}
              className="w-10 h-10 rounded-full border-2 border-white"
              alt="user-profile"
            />
            <span className="truncate">{user.userName}</span>
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-2 mx-3 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold shadow transition"
          >
            <FiLogOut className="text-xl" />
            Logout
          </button>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
